import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, PowerOff, Power, AlertTriangle } from "lucide-react";

/**
 * Global CRM auto-response kill switch.
 * When OFF, new WordPress webhook leads are stored but no auto-email is sent.
 */
export default function AutoResponseToggle() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/lead-settings");
        setSettings(r.data);
        setReason(r.data?.autoresponse_paused_reason || "");
      } catch {
        setSettings({ autoresponse_enabled: true });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (newValue) => {
    setSaving(true);
    try {
      const r = await api.patch("/lead-settings", {
        autoresponse_enabled: newValue,
        reason: newValue ? undefined : (reason || "").trim() || null,
      });
      setSettings(r.data);
      if (newValue) {
        toast.success(t("autoresponse_resumed"));
      } else {
        toast.warning(t("autoresponse_paused"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const saveReason = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/lead-settings", {
        autoresponse_enabled: false,
        reason: (reason || "").trim() || null,
      });
      setSettings(r.data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-center text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        {t("loading") || "..."}
      </div>
    );
  }

  const enabled = !!settings.autoresponse_enabled;

  return (
    <div
      className={`rounded-lg border-2 p-4 md:p-5 transition-colors ${
        enabled
          ? "border-emerald-200 bg-emerald-50/50"
          : "border-amber-300 bg-amber-50"
      }`}
      data-testid="autoresponse-toggle-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div
            className={`p-2 rounded-md shrink-0 ${
              enabled ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"
            }`}
          >
            {enabled ? <Power className="h-5 w-5" /> : <PowerOff className="h-5 w-5" />}
          </div>
          <div>
            <div className="font-semibold text-slate-900">
              {enabled
                ? t("autoresponse_toggle_on_title")
                : t("autoresponse_toggle_off_title")}
            </div>
            <div className="text-sm text-slate-600 mt-0.5">
              {enabled
                ? t("autoresponse_toggle_on_body")
                : t("autoresponse_toggle_off_body")}
            </div>
            {!enabled && settings.autoresponse_paused_reason && (
              <div className="text-xs text-amber-800 mt-2 italic">
                <strong>{t("autoresponse_reason_label")}:</strong>{" "}
                {settings.autoresponse_paused_reason}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">
            {enabled ? t("autoresponse_status_active") : t("autoresponse_status_paused")}
          </span>
          <Switch
            checked={enabled}
            disabled={saving}
            onCheckedChange={toggle}
            data-testid="autoresponse-toggle-switch"
          />
        </div>
      </div>

      {!enabled && (
        <div className="mt-4 pt-4 border-t border-amber-200 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-1" />
          <div className="flex-1">
            <label className="text-xs font-semibold text-amber-900 block mb-1">
              {t("autoresponse_reason_input_label")}
            </label>
            <div className="flex gap-2">
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("autoresponse_reason_placeholder")}
                data-testid="autoresponse-reason-input"
                className="text-sm bg-white"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={saveReason}
                disabled={saving}
                data-testid="autoresponse-save-reason-btn"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("save")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
